import React, { useEffect, useCallback } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Fix default marker icon issue with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const KSB_DEFAULT_CENTER = [-8.5, 117.0];
const DEFAULT_ZOOM = 17;

/**
 * Inner component: fly to position when coordinates change from outside
 */
function MapUpdater({ position, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (position && position[0] !== 0 && position[1] !== 0) {
      map.flyTo(position, zoom || DEFAULT_ZOOM, { duration: 0.8 });
    }
  }, [position, zoom, map]);
  return null;
}

/**
 * Inner component: fix rendering in modals/hidden containers
 */
function MapInvalidator() {
  const map = useMap();
  useEffect(() => {
    const timer = setTimeout(() => {
      map.invalidateSize();
    }, 300);
    return () => clearTimeout(timer);
  }, [map]);
  return null;
}

/**
 * Inner component: handle click on map to place/move marker
 */
function MapClickHandler({ onCoordinateChange }) {
  useMapEvents({
    click(e) {
      const { lat, lng } = e.latlng;
      const coordString = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
      if (onCoordinateChange) {
        onCoordinateChange(coordString);
      }
    },
  });
  return null;
}

const CoordinateMapsVerifikasi = ({ 
  coordinates, // string "lat, lng" or null
  onCoordinateChange, // callback: (coordString: "lat, lng") => void
}) => {
  // Parse coordinate string to [lat, lng] array
  const parseCoordinates = useCallback((coordStr) => {
    if (!coordStr) return null;
    const parts = coordStr.split(",").map((s) => parseFloat(s.trim()));
    if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
      return parts;
    }
    return null;
  }, []);

  const parsedCoords = parseCoordinates(coordinates);
  const mapCenter = parsedCoords || KSB_DEFAULT_CENTER;

  // Satellite tile: Esri World Imagery (free, no API key needed)
  const satelliteUrl =
    "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
  const satelliteAttribution =
    "Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community";

  return (
    <div
      style={{
        width: "100%",
        borderRadius: "8px",
        overflow: "hidden",
        border: "1px solid #ccc",
        marginTop: "8px",
        marginBottom: "8px",
      }}
    >
      <MapContainer
        center={mapCenter}
        zoom={parsedCoords ? DEFAULT_ZOOM : 13}
        style={{ width: "100%", height: "300px" }}
        zoomControl={true}
        dragging={true}
        scrollWheelZoom={true}
        tap={true}
      >
        <TileLayer url={satelliteUrl} attribution={satelliteAttribution} />

        {/* Click handler to place/move marker */}
        <MapClickHandler onCoordinateChange={onCoordinateChange} />
        
        {parsedCoords && (
          <Marker position={parsedCoords} />
        )}

        <MapUpdater position={parsedCoords || KSB_DEFAULT_CENTER} zoom={DEFAULT_ZOOM} />
        <MapInvalidator />
      </MapContainer>

      <div
        style={{
          padding: "6px 10px",
          background: "#f8f9fa",
          fontSize: "12px",
          color: "#555",
          textAlign: "center",
        }}
      >
        {parsedCoords ? (
          <>
            📍 {parsedCoords[0].toFixed(6)}, {parsedCoords[1].toFixed(6)} — 
            <em>Klik pada peta untuk menyesuaikan posisi</em>
          </>
        ) : (
          <>
            <em>Belum ada koordinat. Gunakan "Ambil Koordinat" atau klik pada peta.</em>
          </>
        )}
      </div>
    </div>
  );
};

export default CoordinateMapsVerifikasi;
